<template>
  <div class="card p-10" v-if="formType">
    <RequiredText />

    <form id="estimateForm" @submit.prevent="submitForm">
      <Card class="card p-5">
        <div class="row g-3">
          <Input
              containerClass="col-6"
              name="expense-title"
              type="text"
              :label="$t('expense.label.title')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-sm form-control-solid'"
              :isRequired="true"
              size="lg"
              :placeholder="$t('expense.placeholder.title')"
              v-model="formData.title">
          </Input>

          <InputDate
              v-if="formType === expenseTypesEnums.TRAVEL"
              containerClass="col-12 col-md-3 col-lg-3"
              name="expense-travel-start-date"
              type="text"
              :label="$t('expense.label.travelStartDate')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-sm form-control-solid'"
              :isRequired="true"
              size="lg"
              :placeholder="$t('expense.placeholder.travelStartDate')"
              v-model="formData.travelStartDate" />

          <InputDate
              v-if="formType === expenseTypesEnums.TRAVEL"
              containerClass="col-12 col-md-3 col-lg-3"
              name="expense-travel-end-date"
              type="text"
              :label="$t('expense.label.travelEndDate')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-sm form-control-solid'"
              :isRequired="true"
              size="lg"
              :placeholder="$t('expense.placeholder.travelEndDate')"
              v-model="formData.travelEndDate" />

        </div>
      </Card>


      <Card v-if="formType === expenseTypesEnums.TRAVEL"  class="card my-5 p-5">
        <div class="flex-column">
          <ExpenseCountryForm
              v-for="(country, index) in formData.countriesFees"
              :key="country.country"
              :id="'expense-country-'+index"
              :index="index"
              :countries-fees="country"
              :symbol="discountCurrencyIso"
              @delete-country-row="removeCountryRow"/>
          <div class="justify-content-end align-items-end float-end">
            <a class="btn btn-primary" @click.prevent="addCountryRow">
              {{ $t('expense.addCountryRow') }}
            </a>
          </div>
        </div>
      </Card>

      <Card v-else class="card my-5 p-5">
        <div class="flex-column">
          <ExpenseFeeForm
              v-for="(fee, index) in formData.fees"
              :key="fee?.id+index"
              :id="'expense-fee-'+index"
              :index="index"
              :fee="fee"
              @delete-row="removeFeeRow"/>
          <div class="justify-content-end align-items-end float-end my-5">
            <a class="btn btn-primary" @click.prevent="addFeeRow">
              {{ $t('expense.new.addFee') }}
            </a>
          </div>
        </div>
      </Card>

      <div class="d-flex justify-content-end mt-10">
        <Button type="cancel" btn-class="btn-sm me-2" btn-text="button.cancel" @click.prevent="cancel" size="7px 30px"></Button>
        <Button type="submit" btn-class="btn-sm me-2" btn-text="button.save" size="7px 30px" :isDisabled="submitting" :isLoading="submitting"></Button>
      </div>
    </form>


  </div>
</template>

<script setup>

import {useStore} from "vuex";
import {computed, defineProps, ref, onMounted} from "vue";
import Input from "@/components/Input.vue";
import Card from "@/Modules/CRM/components/Card.vue";
import {useDiscountType} from "@/composables/discountType";
import Button from "@/components/Button.vue";
import {useRoute, useRouter} from 'vue-router';
import RequiredText from "@/components/RequiredText.vue"
import InputDate from "@/components/InputDate.vue";
import {useExpenseEnums} from "@/Modules/Financial/composables/expenseEnums";
import ExpenseCountryForm from "@/Modules/Financial/components/Expense/ExpenseCountryForm.vue";
import ExpenseFeeForm from "@/Modules/Financial/components/Expense/ExpenseFeeForm.vue";

const {discountType} = useDiscountType()
const { expenseTypesEnums } = useExpenseEnums()

const store = useStore()
const router = useRouter()
const route = useRoute()

const props = defineProps({
  isEdit: { type: Boolean, default: false },
  expense: {
    type: Object,
    default: () => ({
      title: '',
      type_id: "",
      countriesFees: [{
        country: '',
        fees: [{
          date: '',
          amount: 0,
          reason_id: '',
          invoices: []
        }]
      }],
      fees: [{
        date: '',
        amount: 0,
        reason_id: '',
        invoices: []
      }]
    })
  },
  submitForm: Function,
  submitting: {type: Boolean, default: false}
})

const formData = ref(props.expense)


const contacts = computed(() => store.getters['contacts/getContacts'])
const taxes = computed(() => store.getters['taxes/getTaxes'])
const countries = computed(() => store.getters['countries/getCountries'])
const currencies = computed(() => store.getters['currencies/getCurrencies'])

const formType = computed(() => {
  if(route.params.id) {
    return props.expense.type
  }
  return route.params.type || ''
})
const discountCurrencyIso = computed(() => {
  if(!formData.value.currency_id) {
    return  discountType.value.fixed.symbol
  }
  return currencies.value.find(el => el.id === formData.value.currency_id)?.iso
})


const addFeeRow = () => {
  formData.value.fees.push({
    date: '',
    amount: 0,
    reason_id: '',
    invoices: []
  })
}
const removeFeeRow = (index) => {
  if (formData.value.fees.length > 1) {
    const [feeToDelete] = formData.value.fees.splice(index, 1)
    if(props.isEdit) {
      formData.value?.feesToDelete.push(feeToDelete?.id)
    }

  }
}
const addCountryRow = () => {
  formData.value.countriesFees.push({
    country: '',
    fees: [{
      date: '',
      amount: 0,
      reason_id: '',
      invoices: []
    }]
  })
}
const removeCountryRow = (index) => {
  if (formData.value.countriesFees.length > 1) {
    formData.value.countriesFees.splice(index, 1)
  }
}


function cancel(){
  router.push({ name: 'expenses'})
}

onMounted(() => {
  store.dispatch('currencies/loadCurrencies')
  store.dispatch('countries/loadCountries')
  store.dispatch('expenses/loadExpenseTypes')
  store.dispatch('reason/loadReasons', props.expense.type_id)
  store.dispatch('reason/loadReasonTypes')
})
</script>

<style scoped>

</style>
